import { assert } from "@faro-lotv/foundation";
import {
  GUID,
  IElement,
  IElementAreaSection,
  IElementGenericImgSheet,
  IElementImg360,
  IElementMarkup,
  IElementSection,
  IElementType,
  IElementTypeHint,
  MarkupIElement,
  WithHint,
  isIElementAreaSection,
  isIElementGenericAnnotation,
  isIElementGenericDataset,
  isIElementGenericImgSheet,
  isIElementImg360,
  isIElementMarkup,
  isIElementOdometryPathSection,
  isIElementPanoInOdometryPath,
  isIElementSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectIElement,
  selectIElementWorldPosition,
  selectRootIElement,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { Vector3 } from "three";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "./ielements-slice-selector";
import { curryAppSelector } from "./reselect";
import { SelectionState, VisibleSheets } from "./selections-slice";
import { RootState } from "./store";

type State = { selections: SelectionState; iElements: IElementsState };

/**
 * @param state Current state
 * @returns The IElement of the current selection
 */
export function selectActiveElement(state: State): IElement | undefined {
  if (!state.selections.activeElement) {
    return;
  }
  return selectIElement(state.selections.activeElement)(state);
}

/**
 * @param state Current state
 * @returns The IElement of the current selection
 */
export function selectActiveElementId({ selections }: State): GUID | undefined {
  return selections.activeElement;
}

/**
 * @param state Current state
 * @returns true if the project is empty (does not contain an Area and an ImgSheet)
 */
export function selectIsProjectEmpty(state: State): boolean {
  return !selectActiveArea(state);
}

/**
 * @param id An Img360 ID or a Section ID that contains an Img360
 * @returns The relevant Img360 and section for the passed id
 */
export function selectPanoAndSection(id: GUID | undefined) {
  return (
    state: RootState,
  ): {
    section: IElementSection | undefined;
    img360: IElementImg360 | undefined;
  } => {
    if (!id) {
      return { section: undefined, img360: undefined };
    }

    const startingElement = selectIElement(id)(state);
    const section = selectAncestor(startingElement, isIElementSection)(state);
    if (!section) {
      throw Error(
        "Passed ID is not an Img360 or a Section containing an Img360",
      );
    }

    const img360 = selectChildDepthFirst(section, isIElementImg360, 1)(state);
    if (!img360) {
      throw Error(
        "Passed ID is not an Img360 or a Section containing an Img360",
      );
    }

    return { section, img360 };
  };
}

/**
 * @returns The closest pano amongst the children of the provided reference element
 * @param referenceId Reference element of the children to check for
 * @param referencePos Position to use as reference
 */
export function selectClosestPanoInSpaceAmongstChildren(
  referenceId: GUID,
  referencePos: Vector3,
) {
  return (state: RootState): IElementImg360 | undefined => {
    const referenceElement = selectIElement(referenceId)(state);

    const panos = selectChildrenDepthFirst(
      referenceElement,
      isIElementImg360,
    )(state);

    return selectClosestElementToPosition(panos, referencePos)(state);
  };
}

/**
 * @returns The closest element to the provided position (in space)
 * @param elements The elements to check
 * @param position The position to find the closest element for
 */
export function selectClosestElementToPosition<T extends IElement>(
  elements: T[],
  position: Vector3,
) {
  return (state: OnlyIElementsStore): T | undefined => {
    let closestElement: T | undefined;
    let closestDistance: number = Infinity;

    const pos = new Vector3();

    for (const element of elements) {
      pos.fromArray(selectIElementWorldPosition(element.id)(state));
      const distance = pos.distanceTo(position);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    }

    return closestElement;
  };
}

/**
 * @param state Current app state
 * @returns The Area (child or parent) related to this element or undefined if no area is available
 */
export const selectActiveArea = createSelector(
  [selectIElementsSlice, selectSelectionsSlice],
  (
    iElements: IElementsState,
    selections: SelectionState,
  ): IElementSection | undefined => {
    if (!selections.activeArea) return;

    const area = iElements.iElements[selections.activeArea];
    if (area && isIElementSection(area)) return area;
  },
);

/**
 * @param element to select the area for
 * @returns the area linked to an element, or undefined if the element is not linked to an area
 */
export function selectAreaFor(element?: IElement) {
  return ({
    iElements,
  }: OnlyIElementsStore): IElementAreaSection | undefined => {
    // Pick the parent area of the element if the element is in the BI tree
    const area = selectAncestor(element, isIElementAreaSection)({ iElements });
    if (area) {
      return area;
    }

    // Pick the area from the first alignment edge for elements in the Capture Tree
    const dataSet = selectAncestor(
      element,
      isIElementGenericDataset,
    )({ iElements });
    const targetAreaId =
      dataSet &&
      Object.entries(iElements.areaDataSets).find(([areaId, dataSets]) => {
        const areaElement = iElements.iElements[areaId];
        if (!areaElement) return false;

        // The queryAreaVolumeInverse of the ProjectAPI client returns the ids of all
        // the elements that have an alignment edge with the required dataset.
        // This means that the iElements.areaDataSets may contain also the datasets
        // associated to cad models. We need to be sure to only pick areas in this selector.
        return (
          isIElementAreaSection(areaElement) &&
          dataSets?.some((value) => value.elementId === dataSet.id)
        );
      })?.[0];
    const targetArea = targetAreaId
      ? selectIElement(targetAreaId)({ iElements })
      : undefined;

    assert(
      !targetArea || isIElementAreaSection(targetArea),
      "A dataset should be linked to a Section(Area)",
    );
    return targetArea;
  };
}

/**
 * @returns the list of markups for the given iElement
 * @param reference An IElement whose children are going to be searched for markups,
 * it can also be an Img360, in that case its sibling tree is checked for markups
 */
export function selectMarkups(reference?: IElement) {
  return (state: OnlyIElementsStore): IElementMarkup[] | undefined => {
    if (!reference) return;
    const parent = isIElementImg360(reference)
      ? selectIElement(reference.parentId ?? "")(state)
      : reference;
    return selectChildrenDepthFirst(parent, isIElementMarkup)(state);
  };
}

/**
 * @param pano reference pano inside an OdometryPath Section
 * @returns The panos in video mode trajectory relative to the reference element
 */
export function selectAllPanosInOdometryPath(
  pano: WithHint<
    IElementImg360,
    | IElementTypeHint.odometryPath
    | IElementTypeHint.poiHighRes
    | IElementTypeHint.poiLowRes
    | IElementTypeHint.poiStandard
    | IElementTypeHint.poi
    | IElementTypeHint.flash
  >,
) {
  return (state: OnlyIElementsStore): IElementImg360[] => {
    if (!pano.parentId) {
      return [];
    }
    const path = selectAncestor(pano, isIElementOdometryPathSection)(state);

    if (!path) return [];

    return selectChildrenDepthFirst(path, isIElementPanoInOdometryPath)(state);
  };
}

/**
 * @param pano to get the section containing its annotation
 * @returns the project Section (or the 360 itself for videomode trajectories) node
 *  containing the annotations for this Pano
 */
export function selectPanoAnnotationContainer(pano: IElementImg360) {
  return (state: OnlyIElementsStore): IElement | undefined => {
    if (selectAncestor(pano, isIElementOdometryPathSection)(state)) return pano;
    return selectAncestor(pano, isIElementSection)(state);
  };
}

/**
 * @returns the project Section node containing the annotations for this Pano
 * @param element element where the annotation was taken on by the user
 * @param area the current area that is used as a fallback if no other section is found
 */
export function selectAnnotationContainer(
  element: IElement,
  area?: IElementSection,
) {
  return (state: RootState): IElement | undefined => {
    // Get the section that is the direct parent of the group containing the annotations:
    // it's a Pano Section if the target element is a 360, otherwise it's either the containing dataset or the area
    if (isIElementImg360(element)) {
      return selectPanoAnnotationContainer(element)(state);
    }
    // If the element is in a dataset, the annotation should be saved there
    return selectAncestor(element, isIElementGenericDataset)(state) ?? area;
  };
}

/**
 * @param markup markup to whose attachments are to be returned
 * @returns list of attachments for the given markup
 */
export function selectMarkupAttachments(markup: MarkupIElement) {
  return (state: RootState): IElement[] => {
    const model3d = selectAncestor(markup, isIElementGenericAnnotation)(state);
    return selectChildrenDepthFirst(
      model3d,
      (el: IElement) =>
        el.typeHint === IElementTypeHint.command ||
        el.type === IElementType.attachment,
    )(state);
  };
}

/**
 * @returns the list of active sheets (memoized = will return the same list if the activeSheets and iElements arrays have not changed)
 * @param state the app state
 * DESIGN NOTES
 * The selector will only return the available sheets. But the returned type is still Array<IElementGenericImgSheet | undefined>
 * as this is the only way for typescript to assume returnedValue[x] might return undefined if x is an invalid index.
 */
export const selectActiveSheetsIfAvailable = createSelector(
  [
    (state: RootState) => state.selections.activeSheets,
    (state: RootState) => state.iElements.iElements,
  ],
  (activeSheets, iElements): Array<IElementGenericImgSheet | undefined> => {
    if (activeSheets.length) {
      return activeSheets
        .map((sheet) => {
          const element = iElements[sheet];
          return isIElementGenericImgSheet(element) ? element : undefined;
        })
        .filter((sheet) => sheet !== undefined);
    }
    return [];
  },
);

/**
 * @returns the list of active sheets or throw an error if one of them is not available
 * (memoized = will return the same list if the activeSheets and iElements arrays have not changed)
 * @param state the app state
 * The selector will only return available sheets. But the returned type is still Array<IElementGenericImgSheet | undefined>
 * as this is the only way for typescript to assume returnedValue[x] might return undefined if x is an invalid index.
 */
export const selectActiveSheets = createSelector(
  [
    (state: RootState) => state.selections.activeSheets,
    (state: RootState) => state.iElements.iElements,
  ],
  (activeSheets, iElements): Array<IElementGenericImgSheet | undefined> => {
    assert(activeSheets.length, "At least one active sheet should exist");

    const listActiveSheets = activeSheets
      .map((sheet) => {
        const element = iElements[sheet];
        return isIElementGenericImgSheet(element) ? element : undefined;
      })
      .filter((sheet) => sheet !== undefined);
    assert(
      listActiveSheets.length === activeSheets.length,
      "One of active sheet is an unsupported format",
    );

    return listActiveSheets;
  },
);

type OnlySelectionsState = { selections: SelectionState };

/**
 * Fetches from the store the list of visible sheets.
 *
 * @param state The current state of the store
 * @returns The list of visible sheets
 */
export function selectVisibleSheetsList({
  selections,
}: OnlySelectionsState): VisibleSheets | undefined {
  return selections.visibleSheets;
}

/**
 * Returns the active area ID.
 *
 * @param state The current selections slice of the store
 * @returns The active area ID
 */
export function selectActiveAreaId({
  selections,
}: OnlySelectionsState): GUID | undefined {
  return selections.activeArea;
}

/**
 * @returns whether this sheet is visible or not
 * @param state the app state
 * @param sheetId GUID of the sheet to be tested
 * DESIGN NOTES
 * The selector is memoized for better performance.
 */
export const selectIsSheetVisible = createSelector(
  [selectVisibleSheetsList, (state: RootState, sheetId: GUID) => sheetId],
  computeIsSheetVisible,
);

/**
 * Computes whether a given sheet is visible.
 *
 * @param visibleSheets The list of visible sheets
 * @param sheetId ID of the required sheet
 * @returns Whether the given sheet is selected as visible or not
 */
export function computeIsSheetVisible(
  visibleSheets: VisibleSheets | undefined,
  sheetId: GUID,
): boolean {
  return visibleSheets ? !!visibleSheets[sheetId] : false;
}

/**
 * @returns The memoized list of visible sheet IDs
 * @param state the app state
 * DESIGN NOTES
 * The selector is memoized for better performance.
 */
export const selectListVisibleSheets = createSelector(
  [(state: RootState) => state.selections.visibleSheets],
  (visibleSheets): GUID[] => {
    if (visibleSheets === undefined) return [];
    return Object.keys(visibleSheets);
  },
);

/** @returns the selections slice of the store */
export function selectSelectionsSlice({
  selections,
}: OnlySelectionsState): SelectionState {
  return selections;
}

/**
 * @returns True if the input iElement has been hidden by the user
 * @param state The root state of the app
 * @param id The id of the iElement to check
 */
export const selectIsScanVisible = curryAppSelector(
  createSelector(
    [
      (state: OnlySelectionsState) => selectSelectionsSlice(state).hiddenScans,
      (state: OnlySelectionsState, id: GUID) => id,
    ],
    (hiddenScans, id: GUID): boolean => !hiddenScans[id],
  ),
);

/**
 * @returns The list of hidden scans
 * @param state The current state of the store
 */
export const selectHiddenScans = createSelector(
  [(state: OnlySelectionsState) => selectSelectionsSlice(state).hiddenScans],
  (hiddenScans): GUID[] => {
    const scans: GUID[] = [];
    for (const [scanId, isHidden] of Object.entries(hiddenScans)) {
      if (isHidden) {
        scans.push(scanId);
      }
    }
    return scans;
  },
);

/**
 * @returns The list of all areas below the given reference element (defaults to the project root)
 * @param state The current state of the store
 * @param refElementId The reference element to start the search from (defaults to the project root)
 */
export const selectAreaSections = createSelector(
  [
    (state: OnlyIElementsStore) => state.iElements,
    (state, refElementId?: GUID) =>
      refElementId
        ? selectIElement(refElementId)(state)
        : selectRootIElement(state),
  ],
  (iElements, refElement) =>
    selectChildrenDepthFirst(refElement, isIElementAreaSection)({ iElements }),
);
